import {
  forwardRef,
  LegacyRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useFloating,
  useInteractions,
  useHover,
  useFocus,
  useRole,
  arrow,
  offset,
  safePolygon,
  flip,
  shift,
} from '@floating-ui/react-dom-interactions';
import cn from 'classnames';
import { decode } from 'html-entities';

import { useSpeakerDetails } from '../../../apis/useSpeakerDetails';
import { useUserProfile } from '../../../apis/useUserSearch';
import I18nContext from '../../../aurr/contexts/I18nContext';
import { encodeBase64 } from '../../../helpers/encode-string';
import Skeleton from '../../presentational/skeleton';

export interface Props {
  oxygenId: string;
  size: 'small' | 'medium' | 'large';
  showPicture: boolean;
  showBio: boolean;
  showFlyout: boolean;
  hasLink: boolean;
  bypassCache: boolean;
  userContentURL?: string;
  userProfileURL?: string;
  showName?: boolean;
  isSpeaker?: boolean;
  darkMode?: boolean;
  showComma?: boolean;
}

type Ref = HTMLAnchorElement | HTMLButtonElement | HTMLSpanElement;

export const placeholderBackgrounds = [
  '#bc2b2b',
  '#871616',
  '#ce6d3c',
  '#527c29',
  '#1d6328',
  '#163b84',
  '#5b5bc2',
  '#571698',
  '#801d94',
  '#be29be',
  '#8f1d69',
  '#c84b75',
];

export const getRandomBackground = () =>
  placeholderBackgrounds[
    Math.floor(Math.random() * placeholderBackgrounds.length)
  ];

function initialsFromName(name: string) {
  const initials = name.match(/\b\w/g) || [];

  return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
}

interface ProfileAvatarProps {
  size?: 'small' | 'medium' | 'large';
  isLoading?: boolean;
  src?: string;
  alt?: string;
  fullName?: string;
  color?: string;
  oxygenId?: string;
  className?: string | Record<string, boolean>;
}

export function ProfileAvatar({
  size = 'medium',
  isLoading,
  src = '',
  alt,
  fullName = '',
  color = '',
  oxygenId,
  className,
}: ProfileAvatarProps) {
  const [randomBackground, setRandomBackground] = useState(
    getRandomBackground()
  );

  /**
   * Monitor localStorage updates to keep all instances of avatar in sync across tabs
   */
  const handleStorageUpdated = (e: StorageEvent) => {
    if (!oxygenId) return;
    const key = `avatar-color-${encodeBase64(oxygenId)}`;
    const storedColor = localStorage.getItem(key);

    // Apply the stored color to all instances of the avatar
    if (e.key === key && storedColor) {
      setRandomBackground(storedColor);
    }
  };

  useEffect(() => {
    if (oxygenId) {
      const key = `avatar-color-${encodeBase64(oxygenId)}`;
      const storedColor = localStorage.getItem(key);
      if (storedColor) {
        setRandomBackground(storedColor);
      } else {
        // If no color is stored for this user ID, generate a new one and store it
        localStorage.setItem(key, randomBackground);
      }
    }

    window.addEventListener('storage', handleStorageUpdated);

    return () => {
      window.removeEventListener('storage', handleStorageUpdated);
    };
  }, [oxygenId]);

  const imgClass = cn('au-rounded-full', className, {
    'au-w-c80 au-min-w-c80 au-h-c80': size === 'large',
    'au-w-c60 au-min-w-c60 au-h-c60': size === 'medium',
    'au-w-c32 au-min-w-c32 au-h-c32': size === 'small',
    'au-text-white au-flex au-items-center au-justify-center': !src,
  });

  if (isLoading) {
    return <Skeleton className={imgClass} rounded />;
  }

  return src ? (
    <img className={imgClass} src={src} alt={alt} />
  ) : (
    <div
      className={imgClass}
      style={{ backgroundColor: color || randomBackground }}
      aria-label={alt}
      role="img"
    >
      {initialsFromName(fullName)}
    </div>
  );
}

interface ProfileBioProps {
  size?: string;
  isLoading: boolean;
  content?: string;
}

function ProfileBio({ content, isLoading, size }: ProfileBioProps) {
  if (isLoading) {
    return <Skeleton width="100%" height={100} />;
  }

  return content ? (
    <div
      className={cn('au-prose au-prose-xl', {
        'au-text-[21px] au-leading-[31px]': size === 'large',
        'au-text-c14 au-leading-c17': size !== 'large',
      })}
    >
      {decode(content, { level: 'html5' })}
    </div>
  ) : null;
}

interface ProfileNameProps {
  className?: string;
  profileURL?: string;
  size?: string;
  isLoading?: boolean;
  fullName?: string;
  hasLink?: boolean;
  showFlyout?: boolean;
  darkMode?: boolean;
  showComma?: boolean;
}

export const ProfileName = forwardRef<Ref, ProfileNameProps>((props, ref) => {
  const {
    className,
    profileURL,
    size,
    isLoading,
    fullName,
    hasLink,
    showFlyout,
    darkMode = false,
    ...restProps
  } = props;

  const nameClass = cn(
    'au-max-w-[100%] au-text-wrap',
    {
      'au-text-[16px] au-leading-c17 au-font-element': size === 'small',
      'au-text-c17 au-leading-c17 au-font-element': size === 'medium',
      'au-text-c18 au-leading-c24 au-text-link-text': size === 'large',
      'hover:au-underline': hasLink,
      'au-text-white': darkMode,
    },
    className
  );

  if (isLoading) {
    return <Skeleton width={200} height={17} />;
  }
  if (hasLink && profileURL) {
    return (
      <a
        href={profileURL}
        ref={ref as unknown as LegacyRef<HTMLAnchorElement>}
        className={nameClass}
        {...restProps}
      >
        {fullName}
        {props.showComma && <span className="au-mr-1.5">,</span>}
      </a>
    );
  }
  if (showFlyout && profileURL) {
    return (
      <button
        type="button"
        ref={ref as unknown as LegacyRef<HTMLButtonElement>}
        className={nameClass}
        {...restProps}
      >
        {fullName}
        {props.showComma && <span className="au-mr-1.5">,</span>}
      </button>
    );
  }
  return (
    <span className={nameClass} ref={ref}>
      {fullName}
      {props.showComma && <span className="au-mr-1.5">,</span>}
    </span>
  );
});

function User({
  oxygenId,
  size,
  showPicture,
  showBio,
  showFlyout,
  hasLink,
  bypassCache,
  userProfileURL,
  userContentURL,
  showName = true,
  isSpeaker = false,
  darkMode = false,
  showComma = false,
}: Props) {
  const {
    data: userProfile,
    isLoading,
    error: userProfileError,
  } = useUserProfile({
    oxygenId,
    bypassCache,
    userProfileURL,
    userContentURL,
  });

  const { data: speakerProfile, isLoading: speakerIsLoading } =
    useSpeakerDetails({
      oxygenId,
      bypassCache,
      userContentURL,
      enabled: showBio || isSpeaker,
    });

  const arrowRef = useRef(null);
  const { t } = useContext(I18nContext);

  const [open, setOpen] = useState(false);
  const {
    context,
    x,
    y,
    reference,
    floating,
    placement,
    strategy,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(12), flip(), shift(), arrow({ element: arrowRef })],
    placement: 'bottom',
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      mouseOnly: true,
      move: false,
      handleClose: safePolygon(),
    }),
    useFocus(context),
    useRole(context),
  ]);

  if (userProfileError && !isSpeaker) {
    return null;
  }

  const {
    encryptedId,
    aboutMe,
    firstName: profileFirstName,
    lastName: profileLastName,
    userName,
    profileImages,
  } = userProfile;
  const {
    biography,
    firstName: speakerFirstName,
    lastName: speakerLastName,
  } = speakerProfile;
  const profileURL = encryptedId
    ? `/autodesk-university/profile/${encodeURIComponent(encryptedId)}`
    : '';

  // Use profile username as backup in case no name details are returned;
  let fullName = userName || '';

  // Cover use-cases of old speaker records that have usercontent data but don't have profile data.
  const firstName = profileFirstName || speakerFirstName || '';
  const lastName = profileLastName || speakerLastName || '';
  if (firstName) {
    // Manually build the person's full name if returned
    fullName = `${firstName}${lastName ? ` ${lastName}` : ''}`;
  }

  const pictureSizeMap = {
    large: profileImages?.sizeX160,
    medium: profileImages?.sizeX120,
    tooltip: profileImages?.sizeX120,
    small: profileImages?.sizeX80,
  };

  const TooltipImage = (
    <ProfileAvatar
      isLoading={false}
      fullName={fullName}
      alt={t('avatarFor', { fullName, ns: 'common' })}
      oxygenId={oxygenId}
      size="medium"
    />
  );

  const Name = (
    <ProfileName
      size={size}
      showFlyout={showFlyout}
      fullName={fullName}
      hasLink={hasLink}
      profileURL={profileURL}
      isLoading={isLoading}
      showComma={showComma}
      ref={reference}
      darkMode={darkMode}
      {...getReferenceProps()}
    />
  );

  const NameLink = (
    <ProfileName
      className="au-font-bold au-text-center lg:au-text-left"
      showFlyout={false}
      fullName={fullName}
      hasLink
      size="large"
      profileURL={profileURL}
      showComma={showComma}
      isLoading={isLoading}
      darkMode={darkMode}
    />
  );

  const Bio = (
    <ProfileBio
      content={aboutMe || biography}
      isLoading={isLoading || speakerIsLoading}
      size={size}
    />
  );

  let LargeContentLine = (
    <div className="au-flex au-flex-col lg:au-pt-[30px]">
      {NameLink}
      <div className="au-mt-[25px] lg:au-mt-[45px]">
        <ProfileBio
          content={aboutMe || biography}
          isLoading={isLoading || speakerIsLoading}
          size={size}
        />
      </div>
    </div>
  );
  if (
    !isLoading &&
    !speakerIsLoading &&
    !(aboutMe || biography) &&
    size === 'large'
  ) {
    LargeContentLine = NameLink;
  }

  const Image = (
    <ProfileAvatar
      src={pictureSizeMap[size]}
      alt={t('avatarFor', { fullName, ns: 'common' })}
      isLoading={isLoading}
      size={size}
      fullName={fullName}
    />
  );

  return (
    <div
      className={cn('au-max-w-[100%] !au-text-white', {
        'au-flex au-flex-col au-gap-c10': showBio && size === 'small',
      })}
    >
      <div
        className={cn('au-flex au-max-w-[100%]', {
          'au-flex-col lg:au-flex-row au-items-center lg:au-items-start au-gap-c13 lg:au-gap-c16':
            showBio && size === 'large' && (aboutMe || biography),
          'au-flex-col lg:au-flex-row au-items-center au-gap-c13 lg:au-gap-c16':
            showBio && size === 'large' && !(aboutMe || biography),
          'au-items-center au-gap-c10':
            showPicture && (size === 'small' || size === 'medium'),
        })}
      >
        {showPicture ? Image : null}
        {showBio && size === 'large' ? LargeContentLine : null}
        {!showBio && showName && size === 'large' ? Name : null}
        {(size === 'small' || size === 'medium') && showName ? Name : null}
      </div>
      {showBio && size === 'small' ? Bio : null}
      {showFlyout && open && (
        <div
          className="au-drop-shadow-user-tooltip au-z-1 !au-bg-[#161616]"
          ref={floating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          {...getFloatingProps()}
        >
          <div className="au-bg-white au-relative au-rounded au-p-c8 au-min-w-c200 !au-bg-[#161616] !au-text-white">
            <div className="au-flex au-gap-c10">
              {TooltipImage}
              <div className="au-flex au-flex-col au-min-w-c200">
                {profileURL ? (
                  <a
                    href={profileURL}
                    className="au-text-blue-400 au-text-c14 au-leading-c17 hover:au-underline"
                  >
                    {fullName}
                  </a>
                ) : (
                  fullName
                )}
                <hr className="au-my-c16" />
              </div>
            </div>
          </div>
          <div
            ref={arrowRef}
            className={cn(
              'au-absolute au-bg-[#161616] au-w-c12 au-h-c12 au-rotate-45',
              {
                'au-translate-y-1/2': placement.includes('top'),
                'au--translate-y-1/2': placement.includes('bottom'),
                'au-translate-x-1/2': placement.includes('left'),
                'au--translate-x-1/2': placement.includes('right'),
              }
            )}
            style={{
              position: 'absolute',
              top: arrowY ?? 0,
              left: arrowX ?? 0,
            }}
          />
        </div>
      )}
    </div>
  );
}
interface UserSkeletonProps {
  size: 'small' | 'large';
  showBio: boolean;
  showPicture: boolean;
}

export function UserSkeleton({
  size,
  showBio,
  showPicture,
}: UserSkeletonProps) {
  const Name = <ProfileName size={size} isLoading />;

  const Bio = <ProfileBio isLoading size={size} />;

  const Image = <ProfileAvatar isLoading size={size} />;

  return (
    <div
      className={cn('', {
        'au-flex au-flex-col au-gap-c10': showBio && size === 'small',
      })}
    >
      <div
        className={cn('au-flex', {
          'au-flex-col lg:au-flex-row au-items-center lg:au-items-start au-gap-c13 lg:au-gap-c36':
            showBio && size === 'large',
          'au-items-center au-gap-c10': showPicture && size === 'small',
        })}
      >
        {showPicture ? Image : null}
        {showBio && size === 'large' ? Bio : null}
        {size === 'small' ? Name : null}
      </div>
      {showBio && size === 'small' ? Bio : null}
    </div>
  );
}

export default User;
